/**
 * Radar — Signal item (used by both EcosystemView and WorldView).
 */
import {
  Building2, ExternalLink, CheckCircle2, MapPin, Target, Coins, ArrowRight,
  TrendingUp, TrendingDown, Activity,
} from "lucide-react";
import { ASYM_META, SECTOR_META, CONTINENT_META } from "./constants";

export function SignalItem({ s, ack, openImpact, showGeo = false }) {
  const score = s.impact?.score_impact;
  const direction = s.impact?.direction;
  return (
    <li
      className={`noam-card p-3 flex items-start gap-3 transition-colors ${s.read ? "opacity-60" : "border-[#EF9F27]/30"}`}
      data-testid={`radar-item-${s.id}`}
    >
      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${s.read ? "bg-zinc-700" : "bg-[#EF9F27] animate-pulse"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-zinc-100 hover:text-[#378ADD] transition-colors"
            >
              {s.title}
            </a>
            <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500 flex-wrap">
              {s.entity_name && (
                <>
                  <Building2 className="w-2.5 h-2.5" />
                  <span>{s.entity_name}</span>
                </>
              )}
              {showGeo && s.geo_continent && (
                <span className="inline-flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5" />
                  {CONTINENT_META[s.geo_continent]?.label || s.geo_continent}
                  {s.geo_country && ` · ${s.geo_country}`}
                </span>
              )}
              {showGeo && s.category && SECTOR_META[s.category] && (
                <span
                  className="px-1 rounded text-[9px]"
                  style={{ color: SECTOR_META[s.category].color, background: `${SECTOR_META[s.category].color}15` }}
                >
                  {SECTOR_META[s.category].label}
                </span>
              )}
              {s.entity_sector && <span>· {s.entity_sector}</span>}
              {s.published_at && <span>· {s.published_at.slice(0, 10)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {typeof score === "number" && (
              <span
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                  direction === "menace" ? "border-red-500/50 text-red-400 bg-red-500/10" :
                  direction === "opportunite" ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10" :
                  "border-[#378ADD]/50 text-[#378ADD] bg-[#378ADD]/10"
                }`}
                title={`Impact ${score}/10 — ${direction || "neutre"}`}
              >
                {direction === "menace" ? <TrendingDown className="w-2.5 h-2.5" /> :
                 direction === "opportunite" ? <TrendingUp className="w-2.5 h-2.5" /> :
                 <Activity className="w-2.5 h-2.5" />}
                {score}/10
              </span>
            )}
            <button
              type="button"
              onClick={() => openImpact(s)}
              className="text-[10px] px-2 py-1 rounded border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/10 inline-flex items-center gap-1"
              data-testid={`radar-impact-${s.id}`}
              title="Croiser ce signal avec ton workspace (annuaire, risques, négos, tickers)"
            >
              <Target className="w-3 h-3" /> Impact sur mon org ?
            </button>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-[#378ADD]"
              title="Ouvrir la source"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            {!s.read && (
              <button
                type="button"
                onClick={() => ack(s.id)}
                className="text-zinc-400 hover:text-[#1D9E75]"
                title="Marquer comme lu"
                data-testid={`radar-ack-${s.id}`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {s.snippet && (
          <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed line-clamp-2">{s.snippet}</p>
        )}
        {s.asymmetry_types?.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {s.asymmetry_types.map((t) => {
              const meta = ASYM_META[t];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold border"
                  style={{ borderColor: `${meta.color}50`, color: meta.color, background: `${meta.color}10` }}
                >
                  <Icon className="w-2.5 h-2.5" /> {meta.label}
                </span>
              );
            })}
          </div>
        )}
        {s.linked_tickers?.length > 0 && (
          <div className="flex items-center gap-1 mt-2 flex-wrap" data-testid={`radar-tickers-${s.id}`}>
            <Coins className="w-2.5 h-2.5 text-[#1D9E75]" />
            <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Marchés fin. :</span>
            {s.linked_tickers.map((t) => (
              <a
                key={t.id}
                href={`/saas/brvm?focus=${t.id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border border-[#1D9E75]/40 text-[#1D9E75] bg-[#1D9E75]/10 hover:bg-[#1D9E75]/20 transition-colors"
                title={`${t.name}${t.exchange ? ` · ${t.exchange}` : ""} — Voir dans Marchés financiers`}
                data-testid={`radar-ticker-link-${t.id}`}
              >
                {t.ticker}
                <ArrowRight className="w-2.5 h-2.5" />
              </a>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}
